import { createSlice } from "@reduxjs/toolkit";
import { TreeData } from "@src/components/tree/TreeTypes";

interface InitialState {
  parentTree: TreeData[];
  pageTree: TreeData[];
  selectedNode: TreeData | null;
  newSelectedNode: TreeData | null;
  tempParentTree: TreeData[];
  tempPageTree: TreeData[];
  syllabusTree: TreeData[];
  tempSyllabusTree: TreeData[];
  selectedNodeName: string;
}

const initialState: InitialState = {
  parentTree: [],
  pageTree: [],
  selectedNode: null,
  newSelectedNode: null,
  tempParentTree: [],
  tempPageTree: [],
  syllabusTree: [],
  tempSyllabusTree: [],
  selectedNodeName: "",
};

const addChildrenRecursively = (
  tree: TreeData[],
  parentId: string,
  children: TreeData[]
): TreeData[] => {
  return tree.map((node) => {
    if (node._id === parentId) {
      const syntheticChildren =
        node.children?.filter(
          (child) => child.type === "VIDEO" || child.type === "ASSIGNMENT"
        ) ?? [];

      return {
        ...node,
        expanded: node.hasChildren ? true : false,
        children: [...children, ...syntheticChildren],
      };
    } else if (node.children) {
      return {
        ...node,
        children: addChildrenRecursively(node.children, parentId, children),
      };
    }
    return node;
  });
};

const toggleExpandedById = (
  arr: TreeData[] | undefined,
  parentId: string | null
): TreeData[] => {
  return arr
    ? arr.map((item) => {
        if (Array.isArray(item.children)) {
          const updatedItem = {
            ...item,
            expanded: item._id === parentId ? !item.expanded : item.expanded,
            children: toggleExpandedById(
              item.children,
              item._id === parentId && !item.expanded ? null : parentId
            ),
          };

          // If parent is collapsed, set all children to false
          if (item._id === parentId && !updatedItem.expanded) {
            updatedItem.children = updatedItem.children.map((child) => ({
              ...child,
              expanded: false,
              children: toggleExpandedById(child.children, null),
            }));
          }

          return updatedItem;
        }
        return item;
      })
    : [];
};

const parentSelectTreeSlice = createSlice({
  name: "selectTree",
  initialState: initialState,
  reducers: {
    setPageTreeData: (state, action) => {
      state.pageTree = action.payload;
    },
    setTempPageTreeData: (state, action) => {
      state.tempPageTree = action.payload;
    },
    setParentTreeData: (state, action) => {
      state.parentTree = action.payload;
    },
    setTempParentTreeData: (state, action) => {
      state.tempParentTree = action.payload;
    },
    setSyllabusTreeData: (state, action) => {
      state.syllabusTree = action.payload;
    },
    setTempSyllabusTreeData: (state, action) => {
      state.tempSyllabusTree = action.payload;
    },
    updateSelectedParent: (state, action) => {
      state.parentTree = action.payload;
      state.tempParentTree = action.payload;
    },
    setSelectedNode: (state, action) => {
      state.selectedNode = action.payload;
    },
    setNewSelectedNode: (state, action) => {
      state.newSelectedNode = action.payload;
    },
    addChildrenToPageTree: (state, action) => {
      const { parentId, children } = action.payload;
      state.pageTree = addChildrenRecursively(
        state.pageTree,
        parentId,
        children
      );
      state.tempPageTree = addChildrenRecursively(
        state.pageTree,
        parentId,
        children
      );
    },
    addChildrenToParentTree: (state, action) => {
      const { parentId, children } = action.payload;
      state.parentTree = addChildrenRecursively(
        state.parentTree,
        parentId,
        children
      );
      state.tempParentTree = addChildrenRecursively(
        state.parentTree,
        parentId,
        children
      );
    },
    collapseExpandInPageTree: (state, action) => {
      state.pageTree = toggleExpandedById(state.pageTree, action.payload._id);
    },
    collapseExpandInParentTree: (state, action) => {
      state.parentTree = toggleExpandedById(
        state.parentTree,
        action.payload._id
      );
    },
    collapseExpandInSyllabusTree: (state, action) => {
      state.syllabusTree = toggleExpandedById(
        state.syllabusTree,
        action.payload._id
      );
    },
    setSelectedNodeName: (state, action) => {
      state.selectedNodeName = action.payload;
    },
    resetTree: (state) => {
      state.pageTree = [];
      state.parentTree = [];
      state.selectedNode = null;
      state.syllabusTree = [];
      state.tempPageTree = [];
      state.tempParentTree = [];
      state.tempSyllabusTree = [];
    },
  },
});

export const {
  setPageTreeData,
  updateSelectedParent,
  addChildrenToPageTree,
  addChildrenToParentTree,
  setParentTreeData,
  collapseExpandInParentTree,
  collapseExpandInPageTree,
  setSelectedNode,
  setTempParentTreeData,
  setTempPageTreeData,
  setSyllabusTreeData,
  collapseExpandInSyllabusTree,
  setTempSyllabusTreeData,
  resetTree,
  setNewSelectedNode,
  setSelectedNodeName,
} = parentSelectTreeSlice.actions;
export default parentSelectTreeSlice.reducer;
